import {
  useListBillingToday,
  getListBillingTodayQueryKey,
  useSendInvoice,
  getListInvoicesQueryKey,
} from "@workspace/api-client-react";
import { useState } from "react";
import { toast } from "sonner";
import { Skeleton } from "@/components/ui/skeleton";
import { Calendar } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { BillingTodayCard } from "./billing-today-card";
import { InvoiceHtmlPreview } from "./invoice-html-preview";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export function TodayBillingDay() {
  const qc = useQueryClient();
  const { data: members, isLoading } = useListBillingToday(undefined, {
    query: { queryKey: getListBillingTodayQueryKey() },
  });
  const [sendingId, setSendingId] = useState<number | null>(null);
  const [previewInvoiceId, setPreviewInvoiceId] = useState<number | null>(null);

  const sendMutation = useSendInvoice({
    mutation: {
      onSuccess: (inv) => {
        toast.success(`Invoice ${inv.invoiceNumber} sent`);
        qc.invalidateQueries({ queryKey: getListBillingTodayQueryKey() });
        qc.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
      },
      onError: (err) => {
        toast.error(`Send failed: ${(err as Error).message}`);
      },
      onSettled: () => setSendingId(null),
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if ((members ?? []).length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <Calendar className="mx-auto h-10 w-10 mb-2" />
        <div className="text-base font-semibold">No billing due today</div>
        <div className="text-sm mt-1">Members billed today will appear here.</div>
      </div>
    );
  }

  return (
    <div>
      {(members ?? []).map((m) => (
        <BillingTodayCard
          key={m.memberId}
          member={{
            ...m,
            existingInvoiceId: m.existingInvoiceId ?? null,
          }}
          isSending={sendingId === m.memberId}
          onSend={() => {
            if (m.existingInvoiceId == null) {
              toast.warning("No invoice exists yet — scheduler will generate at next tick. Try again in a minute.");
              return;
            }
            setSendingId(m.memberId);
            sendMutation.mutate({ id: m.existingInvoiceId });
          }}
          onSkip={() => toast.info("Skip not implemented in v1 — invoice will not auto-resend.")}
          onPreview={() => {
            if (m.existingInvoiceId != null) {
              setPreviewInvoiceId(m.existingInvoiceId); // D-07 HTML preview
            } else {
              toast.warning("Invoice not yet generated.");
            }
          }}
        />
      ))}

      <Dialog open={previewInvoiceId != null} onOpenChange={(o) => { if (!o) setPreviewInvoiceId(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Invoice Preview (HTML)</DialogTitle>
          </DialogHeader>
          {previewInvoiceId != null ? <InvoiceHtmlPreview invoiceId={previewInvoiceId} /> : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
